"use client";

import { useRef, useState, useEffect } from "react";
import Image from "next/image";

/**
 * Product-page phone preview.
 *
 * The invitation is a whole app, and mounting it on page load cost this route
 * up to 14 MB (the Christian template's artwork alone is ~8 MB) and pushed
 * mobile LCP past 6s. It now opens on demand from a still, the same way a video
 * thumbnail does.
 */
export default function TemplatePreview({
  previewSrc,
  name,
  slug,
}: {
  previewSrc: string;
  name: string;
  slug: string;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);
  const [open, setOpen] = useState(false);
  const [frameReady, setFrameReady] = useState(false);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const update = () => setScale(frame.clientWidth / 375);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(frame);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="tp-product__phone-frame" ref={frameRef}>
      <Image
        src={`/assets/homepage/posters/${slug}.webp`}
        alt={`${name} invitation preview`}
        fill
        sizes="(max-width: 768px) 80vw, 375px"
        priority
        className={`tp-product__phone-poster${frameReady ? " is-hidden" : ""}`}
      />

      {open ? (
        <div
          className="tp-product__phone-scaler"
          style={{ transform: `scale(${scale})` }}
        >
          <iframe
            src={previewSrc}
            className="tp-product__phone-iframe"
            title={`${name} Preview`}
            onLoad={() => setFrameReady(true)}
          />
        </div>
      ) : (
        <button
          type="button"
          className="tp-product__phone-play"
          onClick={() => setOpen(true)}
        >
          <span className="tp-product__phone-play-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="6 3 20 12 6 21 6 3" />
            </svg>
          </span>
          Open live preview
        </button>
      )}
    </div>
  );
}
